import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod/v4'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { eq } from 'drizzle-orm'
import { data, redirect, Form, Link, href, useSearchParams } from 'react-router'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { z } from 'zod'
import {
	AuthDivider,
	AuthField,
	AuthShell,
	AuthSubmit,
} from '#app/components/auth/auth-shell.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Honeypot } from '#app/components/forms.tsx'
import { db } from '#app/db/index.ts'
import { users } from '#app/db/schema/index.ts'
import { enforceAbuseLimit } from '#app/utils/abuse-limit.server.ts'
import {
	checkIsCommonPassword,
	extractSetCookieHeaders,
	requireAnonymous,
	signup,
} from '#app/utils/auth.server.ts'
import {
	ProviderConnectionForm,
	providerNames,
} from '#app/utils/connections.tsx'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { getIpAddress } from '#app/utils/request-info.server.ts'
import { noIndexMeta } from '#app/utils/seo.ts'
import {
	MIN_NEW_PASSWORD_LENGTH,
	NameSchema,
	EmailSchema,
	PasswordAndConfirmPasswordSchema,
} from '#app/utils/user-validation.ts'
import { type Route } from './+types/signup.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const SignupSchema = z
	.object({
		email: EmailSchema,
		name: NameSchema,
		redirectTo: z.string().optional(),
	})
	.and(PasswordAndConfirmPasswordSchema)

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	return null
}

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)

	await enforceAbuseLimit('signup', getIpAddress(request))

	const formData = await request.formData()
	await checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			SignupSchema.superRefine(async (formValues, ctx) => {
				if (intent !== null) return

				const isCommonPassword = await checkIsCommonPassword(
					formValues.password,
				)
				if (isCommonPassword) {
					ctx.addIssue({
						path: ['password'],
						code: z.ZodIssueCode.custom,
						message:
							'This password has been compromised in a data breach. Please choose a different password.',
					})
				}
			}).transform(async (formValues) => {
				if (intent !== null) return { ...formValues, signupResult: null }

				const existingUser = await db.query.users.findFirst({
					where: eq(users.email, formValues.email),
					columns: { id: true },
				})
				if (existingUser) {
					return { ...formValues, signupResult: 'existingUser' as const }
				}

				const result = await signup({
					email: formValues.email,
					name: formValues.name,
					password: formValues.password,
					request,
				})

				return { ...formValues, signupResult: result }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.signupResult) {
		return data(
			{
				result: submission.reply({
					hideFields: ['password', 'confirmPassword'],
				}),
				verificationRequired: false as const,
			},
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	if (submission.value.signupResult === 'existingUser') {
		return data({
			result: submission.reply({ hideFields: ['password', 'confirmPassword'] }),
			verificationRequired: true as const,
		})
	}

	const { signupResult, redirectTo } = submission.value

	if ('emailVerificationRequired' in signupResult) {
		return data({
			result: submission.reply({ hideFields: ['password', 'confirmPassword'] }),
			verificationRequired: true as const,
		})
	}

	const headers = extractSetCookieHeaders(signupResult.responseHeaders)

	return redirect(safeRedirect(redirectTo ?? '/dashboard'), { headers })
}

export const meta: Route.MetaFunction = () => {
	return noIndexMeta('Sign up · Syrcha')
}

export default function SignupRoute({ actionData }: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getZodConstraint(SignupSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: SignupSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	if (actionData?.verificationRequired) {
		return (
			<AuthShell
				eyebrow="/ signup · verify"
				title={
					<>
						Check your <span className="accent-underline">inbox</span>.
					</>
				}
				intro="We've sent a verification link to the address you provided. Click it to activate your account - it expires in 24 hours."
				footer={
					<>
						Already verified?{' '}
						<Link
							to={href('/login')}
							className="font-medium text-[var(--marketing-accent)] underline-offset-4 transition-colors hover:text-[var(--marketing-accent-light)] hover:underline"
						>
							Sign in
						</Link>
						.
					</>
				}
			>
				<div className="flex items-start gap-4">
					<span
						aria-hidden
						className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-[var(--marketing-border-strong)] bg-[var(--marketing-surface)] text-[var(--marketing-accent)]"
					>
						<svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
							<path
								d="M3 7l9 6 9-6M5 5h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</span>
					<div className="text-[13px] leading-relaxed text-[var(--marketing-gray)]">
						<p className="text-[var(--marketing-black)]">
							Email sent. Open it on this device to finish signing up.
						</p>
						<p className="mt-2">
							Nothing in your inbox after a couple of minutes? Check spam, then{' '}
							<a
								href="mailto:support@syrcha.com"
								className="text-[var(--marketing-accent)] underline-offset-4 transition-colors hover:text-[var(--marketing-accent-light)] hover:underline"
							>
								email support
							</a>
							.
						</p>
					</div>
				</div>
			</AuthShell>
		)
	}

	return (
		<AuthShell
			eyebrow="/ signup"
			title={
				<>
					Start in <span className="accent-underline">under a minute</span>.
				</>
			}
			intro="Free first scan. No card. You can delete the account from settings whenever you like."
			footer={
				<>
					Already have an account?{' '}
					<Link
						to={
							redirectTo
								? {
										pathname: href('/login'),
										search: `redirectTo=${encodeURIComponent(redirectTo)}`,
									}
								: href('/login')
						}
						className="font-medium text-[var(--marketing-accent)] underline-offset-4 transition-colors hover:text-[var(--marketing-accent-light)] hover:underline"
					>
						Sign in
					</Link>
					.
				</>
			}
		>
			<Form method="POST" {...getFormProps(form)}>
				<Honeypot />

				<AuthField
					id={fields.name.id}
					label="Name"
					error={fields.name.errors?.[0]}
				>
					<input
						{...getInputProps(fields.name, { type: 'text' })}
						autoFocus
						autoComplete="name"
						placeholder="Jane Doe"
						className="auth-input"
					/>
				</AuthField>

				<AuthField
					id={fields.email.id}
					label="Email"
					error={fields.email.errors?.[0]}
				>
					<input
						{...getInputProps(fields.email, { type: 'email' })}
						autoComplete="email"
						placeholder="you@example.com"
						className="auth-input lowercase"
					/>
				</AuthField>

				<AuthField
					id={fields.password.id}
					label="Password"
					error={fields.password.errors?.[0]}
				>
					<input
						{...getInputProps(fields.password, { type: 'password' })}
						autoComplete="new-password"
						placeholder={`At least ${MIN_NEW_PASSWORD_LENGTH} characters`}
						className="auth-input"
					/>
				</AuthField>

				<AuthField
					id={fields.confirmPassword.id}
					label="Confirm password"
					error={fields.confirmPassword.errors?.[0]}
				>
					<input
						{...getInputProps(fields.confirmPassword, { type: 'password' })}
						autoComplete="new-password"
						placeholder="Type it again"
						className="auth-input"
					/>
				</AuthField>

				<input
					{...getInputProps(fields.redirectTo, { type: 'hidden' })}
					value={redirectTo ?? ''}
				/>
				<ErrorList errors={form.errors} id={form.errorId} announce />

				<AuthSubmit isPending={isPending} pendingLabel="Creating account…">
					Create account
				</AuthSubmit>
			</Form>

			<AuthDivider>or</AuthDivider>

			<div className="space-y-2.5">
				{providerNames.map((providerName) => (
					<ProviderConnectionForm
						key={providerName}
						type="Signup"
						providerName={providerName}
						redirectTo={redirectTo}
						className="auth-secondary-btn"
					/>
				))}
			</div>

			<p className="mt-6 text-center text-[11px] leading-relaxed text-[var(--marketing-faint)]">
				By creating an account you agree to our{' '}
				<Link
					to={href('/tos')}
					className="text-[var(--marketing-accent)] underline-offset-4 transition-colors hover:text-[var(--marketing-accent-light)] hover:underline"
				>
					Terms
				</Link>
				,{' '}
				<Link
					to={href('/privacy')}
					className="text-[var(--marketing-accent)] underline-offset-4 transition-colors hover:text-[var(--marketing-accent-light)] hover:underline"
				>
					Privacy
				</Link>{' '}
				and{' '}
				<Link
					to={href('/aup')}
					className="text-[var(--marketing-accent)] underline-offset-4 transition-colors hover:text-[var(--marketing-accent-light)] hover:underline"
				>
					Acceptable Use
				</Link>{' '}
				policies.
			</p>
		</AuthShell>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
